/**
 * ChemicalDosingPage.tsx — Wastewater Chemical Dosing Station
 *
 * Full wastewater chemical dosing calculator:
 *   · Pulls influent sample from active session
 *   · 20+ chemicals across 6 groups (coagulants, flocculants, pH, oxidants, nutrients, other)
 *   · Real-time post-dosing parameter computation
 *   · Saves state to localStorage → ImpellerMixingPage reads it
 *
 * Science references: Metcalf & Eddy 5th Ed.; WEF MOP 8; AWWA Water Quality & Treatment 6th Ed.
 */

import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
  FlaskConical, Droplets, ArrowRight, RotateCcw, ChevronDown, ChevronUp,
  AlertTriangle, CheckCircle2, Info, Layers, Wind, Zap,
  Leaf, Package, ToggleLeft, ToggleRight, Settings, Lightbulb, X,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useSession } from '@/context/SessionContext';
import { getAllLocalSamples } from '@/lib/hybridStorage';
import { toast } from 'sonner';

// ─── Shared State (localStorage) ────────────────────────────────────────────

export const DOSING_STATE_KEY = 'rb_chem_dosing_v1';

export interface ChemDose { id: string; dose: number; enabled: boolean }

export interface DosingState {
  influentSampleId: number | null;
  influentSampleName: string;
  influentParams: Record<string, number>;
  flowRateMH: number;
  chemicals: ChemDose[];
  postDosingParams: Record<string, number>;
  savedAt: string;
}

export function loadDosingState(): DosingState | null {
  try { const r = localStorage.getItem(DOSING_STATE_KEY); return r ? JSON.parse(r) : null; }
  catch { return null; }
}
export function saveDosingState(s: DosingState) {
  try { localStorage.setItem(DOSING_STATE_KEY, JSON.stringify(s)); } catch { /* ignore */ }
}

// ─── Types ───────────────────────────────────────────────────────────────────

type ChemGroup = 'coagulant' | 'flocculant' | 'ph_adj' | 'oxidant' | 'nutrient' | 'other';

interface ChemDef {
  id: string; name: string; formula: string; group: ChemGroup;
  unit: string; min: number; max: number; def: number; step: number;
  color: string; notes: string;
}

// ─── Chemical Library ────────────────────────────────────────────────────────

const CHEMICALS: ChemDef[] = [
  // Coagulants
  { id:'alum',         name:'Alum',                       formula:'Al₂(SO₄)₃·18H₂O', group:'coagulant', unit:'mg/L', min:0, max:300, def:50,  step:5,    color:'#3b82f6', notes:'Standard coagulant. Acid-forming: consumes ~0.45 mg/L alk (CaCO₃) per mg/L. Effective pH 5.5–7.5.' },
  { id:'pac',          name:'Poly-Aluminium Chloride',     formula:'Al_n(OH)_m Cl',    group:'coagulant', unit:'mg/L', min:0, max:200, def:30,  step:5,    color:'#60a5fa', notes:'Pre-hydrolysed Al. Less alkalinity consumption. Broader pH range 5–9.' },
  { id:'fecl3',        name:'Ferric Chloride',             formula:'FeCl₃',             group:'coagulant', unit:'mg/L', min:0, max:200, def:40,  step:5,    color:'#f97316', notes:'Strong coagulant. Consumes ~0.92 mg/L alk per mg/L. Best TP removal.' },
  { id:'feso4',        name:'Ferrous Sulphate',            formula:'FeSO₄·7H₂O',        group:'coagulant', unit:'mg/L', min:0, max:200, def:50,  step:5,    color:'#fb923c', notes:'Requires oxidation to Fe³⁺. Weaker coagulant without aeration/oxidant.' },
  { id:'fe2so43',      name:'Ferric Sulphate',             formula:'Fe₂(SO₄)₃',         group:'coagulant', unit:'mg/L', min:0, max:200, def:40,  step:5,    color:'#ea580c', notes:'Similar to FeCl₃ without Cl addition. Consumes ~0.75 mg/L alk per mg/L.' },
  // Flocculants
  { id:'anionic_poly', name:'Anionic Polymer',             formula:'PAM (anionic)',      group:'flocculant',unit:'mg/L', min:0, max:10,  def:2,   step:0.25, color:'#a78bfa', notes:'High MW polyacrylamide. Used after cationic coagulants. Bridging/floc growth mechanism.' },
  { id:'cationic_poly',name:'Cationic Polymer',            formula:'PAM (cationic)',     group:'flocculant',unit:'mg/L', min:0, max:20,  def:3,   step:0.5,  color:'#c084fc', notes:'Can act as primary coagulant. Effective for negatively charged colloids.' },
  { id:'nonionic_poly',name:'Non-ionic Polymer',           formula:'PAM (non-ionic)',    group:'flocculant',unit:'mg/L', min:0, max:10,  def:2,   step:0.25, color:'#d8b4fe', notes:'Bridging flocculant. Effective over wider pH range.' },
  // pH Adjustment
  { id:'naoh',         name:'Caustic Soda',                formula:'NaOH',              group:'ph_adj',    unit:'mg/L', min:0, max:500, def:50,  step:10,   color:'#10b981', notes:'Strong base. 40 g/mol. Rapid pH rise. Adds Na⁺. 50% liquid solution common.' },
  { id:'caoh2',        name:'Hydrated Lime',               formula:'Ca(OH)₂',           group:'ph_adj',    unit:'mg/L', min:0, max:500, def:80,  step:10,   color:'#6ee7b7', notes:'Slurry application. Adds Ca²⁺ + alkalinity. Lower cost than NaOH.' },
  { id:'na2co3',       name:'Soda Ash',                    formula:'Na₂CO₃',            group:'ph_adj',    unit:'mg/L', min:0, max:500, def:100, step:10,   color:'#34d399', notes:'Mild base. Adds alkalinity. Less pH overshoot risk than NaOH.' },
  { id:'h2so4',        name:'Sulphuric Acid',              formula:'H₂SO₄',             group:'ph_adj',    unit:'mg/L', min:0, max:500, def:50,  step:10,   color:'#ef4444', notes:'Strong acid 98%. Lowers pH rapidly. Adds SO₄²⁻. Consumes ~1.02 mg/L alk per mg/L.' },
  { id:'hcl',          name:'Hydrochloric Acid',           formula:'HCl',               group:'ph_adj',    unit:'mg/L', min:0, max:300, def:30,  step:5,    color:'#f87171', notes:'Strong acid 30–35%. No sulphate addition. Corrosive — dosing care required.' },
  { id:'co2',          name:'Carbon Dioxide (CO₂)',        formula:'CO₂',               group:'ph_adj',    unit:'mg/L', min:0, max:200, def:20,  step:5,    color:'#fca5a5', notes:'Mild acid via H₂CO₃. Precise pH control without overdosing. Adds HCO₃⁻ at pH>6.35.' },
  // Oxidants / Disinfection
  { id:'naocl',        name:'Sodium Hypochlorite',         formula:'NaOCl 12.5%',       group:'oxidant',   unit:'mg/L Cl₂', min:0, max:20,  def:5,  step:0.5, color:'#f59e0b', notes:'Disinfectant. Removes BOD, kills pathogens. pH-dependent: more effective at lower pH.' },
  { id:'h2o2',         name:'Hydrogen Peroxide',           formula:'H₂O₂',             group:'oxidant',   unit:'mg/L', min:0, max:500, def:50,  step:10,   color:'#fbbf24', notes:'Oxidises organics and sulphides. Decomposes to water. Often catalysed by Fe²⁺ (Fenton).' },
  { id:'kmno4',        name:'Potassium Permanganate',      formula:'KMnO₄',            group:'oxidant',   unit:'mg/L', min:0, max:20,  def:5,   step:0.5,  color:'#d946ef', notes:'Strong oxidant for Mn²⁺, Fe²⁺, H₂S. Forms MnO₂ precipitate. 1 mg/L oxidises ~0.25 mg/L Mn.' },
  { id:'cl2',          name:'Chlorine Gas',                formula:'Cl₂',              group:'oxidant',   unit:'mg/L', min:0, max:20,  def:5,   step:0.5,  color:'#fde68a', notes:'Primary disinfectant. Forms HOCl/OCl⁻. Lowers pH slightly. DBP formation potential.' },
  // Nutrients (biological systems)
  { id:'nh4cl',        name:'Ammonium Chloride',           formula:'NH₄Cl',            group:'nutrient',  unit:'mg/L', min:0, max:200, def:50,  step:5,    color:'#06b6d4', notes:'Nitrogen source for biological treatment. 26.2% N by weight. Lowers pH slightly.' },
  { id:'urea',         name:'Urea',                        formula:'CO(NH₂)₂',         group:'nutrient',  unit:'mg/L', min:0, max:500, def:100, step:10,   color:'#22d3ee', notes:'Slow-release N source. 46.7% N. Hydrolyses to NH₃+CO₂. Adds COD load.' },
  { id:'h3po4',        name:'Phosphoric Acid',             formula:'H₃PO₄',           group:'nutrient',  unit:'mg/L', min:0, max:100, def:20,  step:2,    color:'#0891b2', notes:'Phosphorus source. 31.6% P by weight. Lowers pH. Adds P for biological uptake.' },
  { id:'dap',          name:'Di-Ammonium Phosphate',       formula:'(NH₄)₂HPO₄',      group:'nutrient',  unit:'mg/L', min:0, max:100, def:20,  step:2,    color:'#155e75', notes:'Combined N+P source. 21% N, 23.5% P. Used in biological nutrient dosing.' },
  { id:'methanol',     name:'Methanol (Carbon Source)',    formula:'CH₃OH',            group:'nutrient',  unit:'mg/L', min:0, max:500, def:100, step:10,   color:'#67e8f9', notes:'Carbon source for denitrification. ThOD ≈ 1.5 g COD/g methanol. Lowers C/N ratio.' },
  // Other
  { id:'antifoam',     name:'Anti-foam Agent',             formula:'Silicone/organic', group:'other',     unit:'mg/L', min:0, max:100, def:10,  step:1,    color:'#94a3b8', notes:'Reduces foaming. Minimal impact on chemical parameters. Silicone or non-silicone types.' },
  { id:'na2s2o3',      name:'Sodium Thiosulphate',         formula:'Na₂S₂O₃',         group:'other',     unit:'mg/L', min:0, max:50,  def:10,  step:1,    color:'#cbd5e1', notes:'Dechlorination. 1 mg/L neutralises ~0.56 mg/L Cl₂. Adds sulphate.' },
  { id:'pac_carbon',   name:'Powdered Activated Carbon',   formula:'PAC (carbon)',     group:'other',     unit:'mg/L', min:0, max:200, def:20,  step:5,    color:'#475569', notes:'Adsorbs trace organics, micropollutants, taste/odour. Adds TSS load (requires downstream removal).' },
];

const GROUP_META: Record<ChemGroup, { label: string; icon: React.ElementType; color: string }> = {
  coagulant:  { label: 'Coagulants',                  icon: Droplets,    color: '#3b82f6' },
  flocculant: { label: 'Flocculants / Settling Aids',  icon: Layers,      color: '#a78bfa' },
  ph_adj:     { label: 'pH Adjustment',                icon: FlaskConical,color: '#10b981' },
  oxidant:    { label: 'Oxidants & Disinfection',      icon: Zap,         color: '#f59e0b' },
  nutrient:   { label: 'Nutrients (Biological)',        icon: Leaf,        color: '#06b6d4' },
  other:      { label: 'Other Additives',              icon: Package,     color: '#94a3b8' },
};

// ─── Influent Parameter Definitions ─────────────────────────────────────────

const PARAM_DEFS = [
  { key:'ph',             label:'pH',             unit:'',           step:0.1,  lowerBetter:false },
  { key:'turbidity',      label:'Turbidity',      unit:'NTU',        step:1,    lowerBetter:true  },
  { key:'tss',            label:'TSS',            unit:'mg/L',       step:1,    lowerBetter:true  },
  { key:'cod',            label:'COD',            unit:'mg/L',       step:5,    lowerBetter:true  },
  { key:'bod5',           label:'BOD₅',           unit:'mg/L',       step:5,    lowerBetter:true  },
  { key:'tds',            label:'TDS',            unit:'mg/L',       step:10,   lowerBetter:true  },
  { key:'alkalinity',     label:'Alkalinity',     unit:'mg/L CaCO₃', step:5,    lowerBetter:false },
  { key:'totalNitrogen',  label:'Total N',        unit:'mg/L',       step:1,    lowerBetter:true  },
  { key:'ammoniaN',       label:'NH₃-N',          unit:'mg/L',       step:0.5,  lowerBetter:true  },
  { key:'totalPhosphorus',label:'Total P',        unit:'mg/L',       step:0.1,  lowerBetter:true  },
  { key:'temperature',    label:'Temperature',    unit:'°C',         step:0.5,  lowerBetter:false },
  { key:'oilGrease',      label:'Oil & Grease',   unit:'mg/L',       step:1,    lowerBetter:true  },
];

// ─── Calculation Engine ──────────────────────────────────────────────────────

function getDose(doses: ChemDose[], id: string): number {
  const c = doses.find(d => d.id === id);
  return (c?.enabled && c.dose > 0) ? c.dose : 0;
}

function calcPostDosing(
  influent: Record<string, number>,
  doses: ChemDose[],
): Record<string, number> {
  const p = { ...influent };

  const alumDose     = getDose(doses, 'alum');
  const pacDose      = getDose(doses, 'pac');
  const fecl3Dose    = getDose(doses, 'fecl3');
  const feso4Dose    = getDose(doses, 'feso4');
  const fe2so43Dose  = getDose(doses, 'fe2so43');
  const anionPoly    = getDose(doses, 'anionic_poly');
  const catPoly      = getDose(doses, 'cationic_poly');
  const nonPoly      = getDose(doses, 'nonionic_poly');
  const naohDose     = getDose(doses, 'naoh');
  const caoh2Dose    = getDose(doses, 'caoh2');
  const na2co3Dose   = getDose(doses, 'na2co3');
  const h2so4Dose    = getDose(doses, 'h2so4');
  const hclDose      = getDose(doses, 'hcl');
  const co2Dose      = getDose(doses, 'co2');
  const naoclDose    = getDose(doses, 'naocl');
  const h2o2Dose     = getDose(doses, 'h2o2');
  const kmno4Dose    = getDose(doses, 'kmno4');
  const cl2Dose      = getDose(doses, 'cl2');
  const nh4clDose    = getDose(doses, 'nh4cl');
  const ureaDose     = getDose(doses, 'urea');
  const h3po4Dose    = getDose(doses, 'h3po4');
  const dapDose      = getDose(doses, 'dap');
  const methanolDose = getDose(doses, 'methanol');
  // na2s2o3 — dechlorination agent; affects TDS only (handled in TDS delta below)
  const na2s2o3Dose  = getDose(doses, 'na2s2o3');
  void na2s2o3Dose; // consumed in TDS calculation
  const pacCarbonDose= getDose(doses, 'pac_carbon');

  const totalAlDose  = alumDose + pacDose;
  const totalFeDose  = fecl3Dose + feso4Dose + fe2so43Dose;
  const totalCoag    = totalAlDose + totalFeDose;
  const totalPoly    = anionPoly + catPoly + nonPoly;
  const polyBoost    = totalPoly > 0 ? 1.18 : 1.0;

  // ── Turbidity/TSS removal (coagulant S-curve empirical) ─────────────────
  let turbRemoval = 0;
  if (alumDose   > 0) turbRemoval = Math.max(turbRemoval, Math.min(0.94, 0.55 * Math.log10(alumDose   / 3 + 1)));
  if (pacDose    > 0) turbRemoval = Math.max(turbRemoval, Math.min(0.95, 0.60 * Math.log10(pacDose    / 2 + 1)));
  if (fecl3Dose  > 0) turbRemoval = Math.max(turbRemoval, Math.min(0.97, 0.65 * Math.log10(fecl3Dose  / 2 + 1)));
  if (feso4Dose  > 0) turbRemoval = Math.max(turbRemoval, Math.min(0.82, 0.45 * Math.log10(feso4Dose  / 4 + 1)));
  if (fe2so43Dose> 0) turbRemoval = Math.max(turbRemoval, Math.min(0.95, 0.63 * Math.log10(fe2so43Dose/ 2 + 1)));
  turbRemoval = Math.min(0.98, turbRemoval * polyBoost);

  if ((p.turbidity ?? 0) > 0) p.turbidity = Math.max(0.5, (p.turbidity ?? 0) * (1 - turbRemoval));
  if ((p.tss ?? 0) > 0 && totalCoag > 0) {
    p.tss = Math.max(2, (p.tss ?? 0) * (1 - turbRemoval * 0.92));
    p.tss += totalCoag * 0.06;  // residual floc solids
  }
  if (totalPoly > 0 && totalCoag === 0 && (p.tss ?? 0) > 0) {
    p.tss = Math.max(2, (p.tss ?? 0) * (1 - 0.15 * (totalPoly / 5)));
  }

  // ── COD removal ──────────────────────────────────────────────────────────
  const codIn = p.cod ?? 0;
  let codRemoval = 0;
  if (alumDose    > 0) codRemoval += Math.min(0.40, alumDose    * 0.0020);
  if (pacDose     > 0) codRemoval += Math.min(0.42, pacDose     * 0.0022);
  if (fecl3Dose   > 0) codRemoval += Math.min(0.45, fecl3Dose   * 0.0025);
  if (feso4Dose   > 0) codRemoval += Math.min(0.30, feso4Dose   * 0.0015);
  if (fe2so43Dose > 0) codRemoval += Math.min(0.43, fe2so43Dose * 0.0022);
  if (h2o2Dose    > 0) codRemoval += Math.min(0.35, h2o2Dose    * 0.0012);
  if (naoclDose   > 0) codRemoval += Math.min(0.20, naoclDose   * 0.0080);
  if (cl2Dose     > 0) codRemoval += Math.min(0.20, cl2Dose     * 0.0080);
  if (pacCarbonDose>0) codRemoval += Math.min(0.40, pacCarbonDose* 0.0035);
  codRemoval = Math.min(0.90, codRemoval);
  p.cod = Math.max(0, codIn * (1 - codRemoval));
  p.cod += methanolDose * 1.50;  // ThOD: 1.5 g COD per g methanol
  p.cod += ureaDose     * 0.53;  // approx COD contribution

  // ── BOD ─────────────────────────────────────────────────────────────────
  const bodIn = p.bod5 ?? 0;
  if (bodIn > 0) {
    p.bod5 = Math.max(0, bodIn * (1 - codRemoval * 0.80));
    p.bod5 += methanolDose * 1.00;
  }

  // ── Alkalinity balance → pH ──────────────────────────────────────────────
  const alkIn = p.alkalinity ?? 150;  // mg/L as CaCO₃
  // Net alkalinity change (mg/L CaCO₃):
  // Coagulants consume alkalinity; bases add; acids remove
  const netAlk =
    naohDose   * 1.250 +   // NaOH: 1 mg/L = 1.25 mg/L CaCO₃
    caoh2Dose  * 1.351 +   // Ca(OH)₂: 1 mg/L = 50/37 mg/L CaCO₃
    na2co3Dose * 0.943 -   // Na₂CO₃: 1 mg/L = 50/53 mg/L CaCO₃
    alumDose   * 0.450 -   // Alum consumes ~0.45 mg/L CaCO₃ per mg/L
    pacDose    * 0.300 -
    fecl3Dose  * 0.920 -   // FeCl₃ consumes ~0.92
    feso4Dose  * 0.360 -
    fe2so43Dose* 0.750 -
    h2so4Dose  * 1.020 -   // H₂SO₄: 1 mg/L = 1.02 mg/L CaCO₃ consumed
    hclDose    * 1.370 -   // HCl: 1 mg/L = 50/36.5 mg/L CaCO₃ consumed
    co2Dose    * 1.136;    // CO₂: 1 mg/L = 50/44 mg/L CaCO₃ consumed

  const newAlk = Math.max(0, alkIn + netAlk);
  p.alkalinity = newAlk;

  // pH estimation (Henderson-Hasselbalch buffer model, simplified)
  // Net acid/base in meq/L (positive = base)
  const netAcidBase_meq = netAlk / 50;
  // Buffer capacity β ≈ 2.3 × (2 × Kw/[H+] + Ca × Ka1 × [H+]/(Ka1+[H+])² + ...)
  // Simplified empirical: β at pH 7 ≈ 0.0005 × alkIn (meq/L per pH unit)
  const currentPH = p.ph ?? 7.0;
  const beta = Math.max(0.001, 0.0006 * alkIn + 0.002);  // meq/L/ΔpH
  const rawDeltaPH = netAcidBase_meq / beta;
  const deltaPH = Math.sign(rawDeltaPH) * Math.min(Math.abs(rawDeltaPH), 4);
  p.ph = Math.min(12.5, Math.max(2.0, currentPH + deltaPH));

  // ── TDS impact ───────────────────────────────────────────────────────────
  const tdsDelta =
    alumDose * 0.80 + pacDose * 0.60 + fecl3Dose * 0.85 + feso4Dose * 0.80 + fe2so43Dose * 0.80 +
    naohDose * 0.75 + caoh2Dose * 0.35 + na2co3Dose * 0.65 +
    h2so4Dose * 0.80 + hclDose * 0.75 +
    nh4clDose * 0.95 + h3po4Dose * 0.75 + dapDose * 0.90 + ureaDose * 0.45;
  if ((p.tds ?? 0) >= 0) p.tds = Math.max(0, (p.tds ?? 0) + tdsDelta);

  // ── Total Phosphorus ─────────────────────────────────────────────────────
  const tpIn = p.totalPhosphorus ?? 0;
  const tpRemoval =
    alumDose   > 0 ? Math.min(0.93, alumDose   * 0.0085) :
    fecl3Dose  > 0 ? Math.min(0.96, fecl3Dose  * 0.0095) :
    fe2so43Dose> 0 ? Math.min(0.94, fe2so43Dose* 0.0090) :
    pacDose    > 0 ? Math.min(0.88, pacDose     * 0.0070) : 0;
  const tpAdded = h3po4Dose * (31 / 98) + dapDose * (62 / 132);  // P fractions
  p.totalPhosphorus = Math.max(0, tpIn * (1 - tpRemoval)) + tpAdded;

  // ── Total Nitrogen & Ammonia ─────────────────────────────────────────────
  const tnAdded  = nh4clDose * (14 / 53.5) + ureaDose * (28 / 60) + dapDose * (28 / 132);
  const nh3Added = nh4clDose * (14 / 53.5);
  p.totalNitrogen = Math.max(0, (p.totalNitrogen ?? 0)) + tnAdded;
  p.ammoniaN      = Math.max(0, (p.ammoniaN ?? 0))      + nh3Added;

  // ── KMnO₄ — Mn/Fe/H₂S oxidation ─────────────────────────────────────────
  if (kmno4Dose > 0) {
    p.cod  = Math.max(0, (p.cod  ?? 0) - kmno4Dose * 0.35);
  }

  // ── Oil & Grease (coagulant removes emulsified O&G) ──────────────────────
  if (totalCoag > 0 && (p.oilGrease ?? 0) > 0) {
    p.oilGrease = Math.max(0, (p.oilGrease ?? 0) * (1 - Math.min(0.80, totalCoag * 0.004)));
  }

  return p;
}

// ─── Default Influent (blank) ─────────────────────────────────────────────────

const BLANK_INFLUENT: Record<string, number> = {
  ph: 7.0, turbidity: 100, tss: 150, cod: 300, bod5: 150,
  tds: 800, alkalinity: 150, totalNitrogen: 40, ammoniaN: 25,
  totalPhosphorus: 5, temperature: 20, oilGrease: 20,
};

// ─── Helper: format number ───────────────────────────────────────────────────
const fv = (v: number, d = 1) => isFinite(v) && !isNaN(v) ? v.toFixed(d) : '—';

// ─── Dosing Scenarios ────────────────────────────────────────────────────────

interface DosingScenario {
  id: string;
  label: string;
  tag: string;
  tagColor: string;
  icon: string;          // emoji
  description: string;
  rationale: string;
  chemicals: { id: string; dose: number }[];
  outcome: Record<string, number>;   // post-dosing params for display
}

function buildDosingScenarios(
  influent: Record<string, number>,
): DosingScenario[] {
  const ph   = influent.ph ?? 7.0;
  const turb = influent.turbidity ?? 100;
  const tp   = influent.totalPhosphorus ?? 5;
  const tn   = influent.totalNitrogen ?? 40;
  const bod  = influent.bod5 ?? 150;
  const alk  = influent.alkalinity ?? 150;

  // ── Scenario A: Coagulation & Clarification ─────────────────────────────
  const alumDoseA  = Math.min(150, Math.max(20, turb * 0.65));
  const polyDoseA  = 1.5;
  const chemsA: { id: string; dose: number }[] = [
    { id: 'alum',         dose: alumDoseA },
    { id: 'anionic_poly', dose: polyDoseA },
  ];
  // pH correction after alum (alum drops pH — add base if needed)
  const alkAfterA = Math.max(0, alk - alumDoseA * 0.45);
  const phAfterAlum = ph - (alumDoseA * 0.45 / Math.max(0.001, 0.0006 * alk + 0.002)) / 50;
  if (phAfterAlum < 6.5) {
    // need lime or soda ash to bring back to ~7.0
    const alkNeeded = (7.0 - phAfterAlum) * (0.0006 * alkAfterA + 0.002) * 50;
    const caoh2A = Math.min(200, Math.max(20, alkNeeded / 1.351));
    chemsA.push({ id: 'caoh2', dose: Math.round(caoh2A / 5) * 5 });
  } else if (phAfterAlum > 8.0) {
    chemsA.push({ id: 'co2', dose: Math.min(100, Math.max(20, (phAfterAlum - 7.5) * 20)) });
  }
  const dosesA: ChemDose[] = chemsA.map(c => ({ id: c.id, dose: c.dose, enabled: true }));

  // ── Scenario B: Phosphorus Removal ──────────────────────────────────────
  const fecl3DoseB = Math.min(150, Math.max(20, tp * 16.5)); // molar excess for >90% removal
  const polyDoseB  = 2.0;
  const chemsB: { id: string; dose: number }[] = [
    { id: 'fecl3',        dose: fecl3DoseB },
    { id: 'anionic_poly', dose: polyDoseB  },
  ];
  const phDropB = fecl3DoseB * 0.92 / Math.max(0.001, 0.0006 * alk + 0.002) / 50;
  if (ph - phDropB < 6.0) {
    const naohB = Math.min(200, Math.max(30, (6.5 - (ph - phDropB)) * 40));
    chemsB.push({ id: 'naoh', dose: Math.round(naohB / 10) * 10 });
  }
  const dosesB: ChemDose[] = chemsB.map(c => ({ id: c.id, dose: c.dose, enabled: true }));

  // ── Scenario C: Biological Pre-treatment Prep ────────────────────────────
  const pacDoseC  = Math.min(80, Math.max(10, turb * 0.30));
  const chemsC: { id: string; dose: number }[] = [
    { id: 'pac', dose: pacDoseC },
  ];
  // Nutrient balance check: BOD:N should be ~20:1, BOD:P ~100:1
  const bodNRatio = tn > 0 ? bod / tn : 0;
  const bodPRatio = tp > 0 ? bod / tp : 0;
  if (bodNRatio > 25 && tn < 40) {
    // N-limited — add ammonium chloride
    const nNeeded = (bod / 20) - tn;
    chemsC.push({ id: 'nh4cl', dose: Math.min(150, Math.max(10, Math.round(nNeeded * 53.5 / 14 / 5) * 5)) });
  }
  if (bodPRatio > 120 && tp < 5) {
    // P-limited — add phosphoric acid
    const pNeeded = (bod / 100) - tp;
    chemsC.push({ id: 'h3po4', dose: Math.min(80, Math.max(5, Math.round(pNeeded * 98 / 31 / 2) * 2)) });
  }
  // pH to bio-optimal 6.8–7.5
  if (ph < 6.8) {
    chemsC.push({ id: 'na2co3', dose: Math.min(150, Math.max(30, (7.0 - ph) * 60)) });
  } else if (ph > 8.0) {
    chemsC.push({ id: 'co2', dose: Math.min(100, Math.max(15, (ph - 7.5) * 25)) });
  }
  const dosesC: ChemDose[] = chemsC.map(c => ({ id: c.id, dose: c.dose, enabled: true }));

  return [
    {
      id: 'coag_clarif',
      label: 'Coagulation & Clarification',
      tag: 'Option A',
      tagColor: '#3b82f6',
      icon: '💧',
      description: 'Alum-based coagulation targeting turbidity/TSS removal with polymer aid. pH-corrected for optimal coagulant performance.',
      rationale: `Alum ${alumDoseA.toFixed(0)} mg/L (≈ turbidity × 0.65), anionic polymer ${polyDoseA} mg/L. Targets suspended solids, colloids, emulsified O&G.`,
      chemicals: chemsA,
      outcome: calcPostDosing(influent, dosesA),
    },
    {
      id: 'phos_removal',
      label: 'Phosphorus Removal',
      tag: 'Option B',
      tagColor: '#f97316',
      icon: '🧪',
      description: 'FeCl₃-driven chemical phosphorus precipitation targeting >90 % TP removal with floc aid. Suitable for discharge compliance.',
      rationale: `FeCl₃ ${fecl3DoseB.toFixed(0)} mg/L (≈ TP × 16.5 molar excess), anionic polymer ${polyDoseB} mg/L. Stoichiometric Fe:P ratio drives FePO₄ precipitation.`,
      chemicals: chemsB,
      outcome: calcPostDosing(influent, dosesB),
    },
    {
      id: 'bio_prep',
      label: 'Biological Pre-treatment Prep',
      tag: 'Option C',
      tagColor: '#10b981',
      icon: '🌿',
      description: 'Light PAC clarification + nutrient balance for biological treatment optimisation. Targets BOD:N:P = 100:5:1 and bio-optimal pH 6.8–7.5.',
      rationale: `PAC ${pacDoseC.toFixed(0)} mg/L partial clarification${bodNRatio > 25 ? ', N supplement for BOD:N balance' : ''}${bodPRatio > 120 ? ', P supplement for BOD:P balance' : ''}. Preserves biodegradable organics for downstream biology.`,
      chemicals: chemsC,
      outcome: calcPostDosing(influent, dosesC),
    },
  ];
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ParamDelta({ label, before, after, unit, lowerBetter }: {
  label: string; before: number; after: number; unit: string; lowerBetter: boolean;
}) {
  const diff = after - before;
  const pct  = before !== 0 ? (diff / before) * 100 : 0;
  const better = lowerBetter ? diff <= 0 : diff >= 0;
  const noChange = Math.abs(diff) < 0.001;

  const color = noChange ? 'text-slate-500' : better ? 'text-emerald-400' : 'text-rose-400';
  const arrow = noChange ? '—' : diff > 0 ? '▲' : '▼';

  const dp = (v: number) => v < 0.1 ? 3 : v < 10 ? 2 : v < 100 ? 1 : 0;

  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-rb-elevated border border-slate-800/60 gap-2 text-xs">
      <span className="text-slate-400 shrink-0 w-28 truncate">{label}</span>
      <div className="flex items-center gap-1.5 flex-1 justify-end font-mono">
        <span className="text-slate-500">{fv(before, dp(before))}</span>
        {unit && <span className="text-slate-700 text-[10px]">{unit}</span>}
        <span className="text-slate-700">→</span>
        <span className={`font-bold ${color}`}>{fv(after, dp(after))}</span>
        {!noChange && <span className={`text-[10px] ${color}`}>{arrow} {Math.abs(pct).toFixed(0)}%</span>}
      </div>
    </div>
  );
}

function ChemicalRow({ def, dose, enabled, onToggle, onChange }: {
  def: ChemDef; dose: number; enabled: boolean;
  onToggle: () => void; onChange: (v: number) => void;
}) {
  return (
    <div className={`p-3 rounded-xl border transition-all ${
      enabled ? 'border-slate-700/60 bg-rb-elevated' : 'border-slate-800/40 bg-rb-surface/50 opacity-60'
    }`}>
      <div className="flex items-center gap-2 mb-2">
        <button onClick={onToggle} className="shrink-0">
          {enabled
            ? <ToggleRight className="w-5 h-5" style={{ color: def.color }} />
            : <ToggleLeft className="w-5 h-5 text-slate-600" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-bold text-slate-200">{def.name}</span>
            <span className="text-[10px] text-slate-500 font-mono">{def.formula}</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <input
            type="number" value={dose} min={def.min} max={def.max} step={def.step}
            disabled={!enabled}
            onChange={e => onChange(parseFloat(e.target.value) || 0)}
            className="w-20 text-right text-xs px-2 py-0.5 text-slate-100 bg-rb-surface border border-slate-700/60 rounded-lg focus:outline-none focus:border-sky-500"
          />
          <span className="text-[10px] text-slate-500 w-12 shrink-0">{def.unit}</span>
        </div>
      </div>
      {enabled && (
        <div className="pl-7">
          <input
            type="range" min={def.min} max={def.max} step={def.step} value={dose}
            onChange={e => onChange(parseFloat(e.target.value))}
            className="w-full h-1.5 cursor-pointer rounded-full"
            style={{ accentColor: def.color }}
          />
          <p className="text-[10px] text-slate-600 mt-1 leading-relaxed">{def.notes}</p>
        </div>
      )}
    </div>
  );
}

// ─── Dosing Scenario Modal ────────────────────────────────────────────────────

const SCENARIO_KEY_PARAMS = [
  { key: 'ph',              label: 'pH',       unit: '',          lowerBetter: false },
  { key: 'turbidity',       label: 'Turbidity',unit: 'NTU',       lowerBetter: true  },
  { key: 'tss',             label: 'TSS',      unit: 'mg/L',      lowerBetter: true  },
  { key: 'cod',             label: 'COD',      unit: 'mg/L',      lowerBetter: true  },
  { key: 'totalPhosphorus', label: 'Total P',  unit: 'mg/L',      lowerBetter: true  },
];

function ScenarioCard({ scenario, influent, selected, onSelect }: {
  scenario: DosingScenario;
  influent: Record<string, number>;
  selected: boolean;
  onSelect: () => void;
}) {
  const borderColor = selected ? scenario.tagColor : '#334155';
  const bgColor     = selected ? `${scenario.tagColor}12` : 'transparent';

  return (
    <div
      onClick={onSelect}
      className="cursor-pointer rounded-2xl border-2 p-4 space-y-3 transition-all hover:scale-[1.01]"
      style={{ borderColor, background: bgColor }}
    >
      {/* Header */}
      <div className="flex items-start gap-2.5">
        <span className="text-2xl leading-none mt-0.5">{scenario.icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-[10px] font-bold px-2 py-0.5 rounded-full"
              style={{ background: `${scenario.tagColor}25`, color: scenario.tagColor, border: `1px solid ${scenario.tagColor}40` }}
            >
              {scenario.tag}
            </span>
            <span className="text-sm font-bold text-slate-100">{scenario.label}</span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1 leading-relaxed">{scenario.description}</p>
        </div>
      </div>

      {/* Chemical recipe chips */}
      <div className="flex flex-wrap gap-1">
        {scenario.chemicals.map(c => {
          const def = CHEMICALS.find(x => x.id === c.id);
          if (!def) return null;
          return (
            <span key={c.id}
              className="text-[10px] font-mono px-2 py-0.5 rounded-lg border border-slate-700/60 bg-rb-elevated text-slate-300">
              {def.name}: <strong style={{ color: def.color }}>{c.dose}</strong> {def.unit}
            </span>
          );
        })}
      </div>

      {/* Before → After for key params */}
      <div className="grid grid-cols-5 gap-1">
        {SCENARIO_KEY_PARAMS.map(pd => {
          const before = influent[pd.key] ?? 0;
          const after  = scenario.outcome[pd.key] ?? before;
          const diff   = after - before;
          const better = pd.lowerBetter ? diff <= 0 : diff >= 0;
          const noChg  = Math.abs(diff) < 0.01;
          const col    = noChg ? 'text-slate-500' : better ? 'text-emerald-400' : 'text-rose-400';
          const dp     = (v: number) => v < 0.1 ? 2 : v < 10 ? 1 : 0;
          return (
            <div key={pd.key} className="text-center p-1.5 rounded-lg bg-rb-elevated border border-slate-800/50 space-y-0.5">
              <div className="text-[9px] text-slate-500 uppercase">{pd.label}</div>
              <div className="text-[11px] font-mono text-slate-400">{fv(before, dp(before))}</div>
              <div className="text-[10px] text-slate-600">↓</div>
              <div className={`text-[11px] font-mono font-bold ${col}`}>{fv(after, dp(after))}</div>
              {pd.unit && <div className="text-[9px] text-slate-700">{pd.unit}</div>}
            </div>
          );
        })}
      </div>

      {/* Rationale */}
      <p className="text-[10px] text-slate-500 leading-relaxed border-t border-slate-800/50 pt-2">
        {scenario.rationale}
      </p>

      {/* Selection indicator */}
      <div className={`flex items-center justify-center gap-1.5 py-1.5 rounded-xl text-xs font-bold transition-colors ${
        selected
          ? 'text-slate-900'
          : 'text-slate-500 bg-rb-elevated border border-slate-700/40'
      }`}
        style={selected ? { background: scenario.tagColor } : {}}
      >
        {selected ? '✓ Selected' : 'Select this scenario'}
      </div>
    </div>
  );
}

function DosingScenarioModal({ scenarios, influent, onApply, onClose }: {
  scenarios: DosingScenario[];
  influent: Record<string, number>;
  onApply: (s: DosingScenario) => void;
  onClose: () => void;
}) {
  const [selected, setSelected] = useState<string>(scenarios[0]?.id ?? '');

  const chosenScenario = scenarios.find(s => s.id === selected) ?? scenarios[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)' }}>
      <div className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-2xl border border-slate-700/60 bg-rb-bg shadow-2xl">

        {/* Modal header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-slate-800/50 bg-rb-bg">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-amber-400/15 border border-amber-400/30 flex items-center justify-center">
              <Lightbulb className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <div className="text-sm font-bold text-slate-100">Dosing Suggestions</div>
              <div className="text-[11px] text-slate-500">3 treatment scenarios — choose one to auto-configure chemicals</div>
            </div>
          </div>
          <button onClick={onClose}
            className="p-1.5 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-rb-elevated transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scenario cards */}
        <div className="p-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {scenarios.map(s => (
            <ScenarioCard
              key={s.id}
              scenario={s}
              influent={influent}
              selected={s.id === selected}
              onSelect={() => setSelected(s.id)}
            />
          ))}
        </div>

        {/* Apply footer */}
        <div className="sticky bottom-0 flex items-center justify-between px-5 py-4 border-t border-slate-800/50 bg-rb-bg gap-3">
          <p className="text-[11px] text-slate-500">
            Applying a scenario will <strong className="text-slate-400">enable</strong> the recommended chemicals and set their doses.
            All other chemicals will be disabled.
          </p>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={onClose}
              className="px-4 py-2 text-xs font-bold text-slate-300 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-xl transition-colors">
              Cancel
            </button>
            <button
              onClick={() => { if (chosenScenario) onApply(chosenScenario); }}
              className="px-5 py-2 text-xs font-bold text-black bg-amber-400 hover:bg-amber-300 rounded-xl transition-colors flex items-center gap-1.5 shadow-sm"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Apply {chosenScenario?.tag}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ChemicalDosingPage() {
  const navigate      = useNavigate();
  const { currentCustomer: _cust } = useSession();
  void _cust;

  // Influent samples from session
  const allSamples = useMemo(() => {
    const s = getAllLocalSamples();
    return s.filter(x => x.sampleType === 'influent' || !x.sampleType)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, []);

  const [selectedId, setSelectedId]    = useState<number | null>(allSamples[0]?.id ?? null);
  const [flowRateMH, setFlowRateMH]    = useState(10);
  const [openGroups, setOpenGroups]    = useState<Record<ChemGroup, boolean>>({
    coagulant: true, flocculant: false, ph_adj: false, oxidant: false, nutrient: false, other: false,
  });

  // Chemical doses state
  const [doses, setDoses] = useState<ChemDose[]>(
    CHEMICALS.map(c => ({ id: c.id, dose: c.def, enabled: false }))
  );

  const [showScenarios, setShowScenarios] = useState(false);

  // Influent — from selected sample or blank
  const influentParams = useMemo((): Record<string, number> => {
    const sample = allSamples.find(s => s.id === selectedId);
    if (!sample) return BLANK_INFLUENT;
    const raw = sample.params ?? {};
    return {
      ph:             raw.ph             ?? 7.0,
      turbidity:      raw.turbidity      ?? raw.tss ?? 100,
      tss:            raw.tss            ?? 150,
      cod:            raw.cod            ?? 300,
      bod5:           raw.bod5 ?? raw.bod ?? 150,
      tds:            raw.tds            ?? 800,
      alkalinity:     raw.alkalinity     ?? 150,
      totalNitrogen:  raw.totalNitrogen  ?? raw.tn ?? 40,
      ammoniaN:       raw.ammoniaN ?? raw.nh3n ?? 25,
      totalPhosphorus:raw.totalPhosphorus ?? raw.tp ?? 5,
      temperature:    raw.temperature    ?? 20,
      oilGrease:      raw.oilGrease ?? raw.og ?? 20,
    };
  }, [selectedId, allSamples]);

  const postDosingParams = useMemo(() => calcPostDosing(influentParams, doses), [influentParams, doses]);

  const dosingScenarios  = useMemo(() => buildDosingScenarios(influentParams), [influentParams]);

  const enabledChemicals = doses.filter(d => d.enabled && d.dose > 0);
  const totalDose = enabledChemicals.reduce((a, d) => a + d.dose, 0);

  function toggleGroup(g: ChemGroup) { setOpenGroups(prev => ({ ...prev, [g]: !prev[g] })); }
  function toggleChem(id: string) {
    setDoses(prev => prev.map(d => d.id === id ? { ...d, enabled: !d.enabled } : d));
  }
  function setDose(id: string, dose: number) {
    setDoses(prev => prev.map(d => d.id === id ? { ...d, dose: Math.max(0, dose) } : d));
  }
  function resetAll() {
    setDoses(CHEMICALS.map(c => ({ id: c.id, dose: c.def, enabled: false })));
    toast.success('All chemicals reset');
  }

  function applyScenario(scenario: DosingScenario) {
    setDoses(CHEMICALS.map(c => {
      const sc = scenario.chemicals.find(x => x.id === c.id);
      return sc
        ? { id: c.id, dose: sc.dose, enabled: true }
        : { id: c.id, dose: c.def, enabled: false };
    }));
    // Open the groups that have active chemicals
    const activeGroups = new Set(
      scenario.chemicals.map(sc => CHEMICALS.find(c => c.id === sc.id)?.group).filter(Boolean)
    );
    setOpenGroups(prev => {
      const next = { ...prev };
      (Object.keys(next) as ChemGroup[]).forEach(g => { next[g] = activeGroups.has(g); });
      return next;
    });
    setShowScenarios(false);
    toast.success(`Applied: ${scenario.label}`);
  }

  const handleProceedToMixing = useCallback(() => {
    const sample = allSamples.find(s => s.id === selectedId);
    const state: DosingState = {
      influentSampleId:   selectedId,
      influentSampleName: sample?.sampleName ?? 'Manual Entry',
      influentParams,
      flowRateMH,
      chemicals: doses.filter(d => d.enabled),
      postDosingParams,
      savedAt: new Date().toISOString(),
    };
    saveDosingState(state);
    toast.success('Dosing data saved — loading mixing page');
    navigate('/impeller-mixing');
  }, [selectedId, allSamples, influentParams, flowRateMH, doses, postDosingParams, navigate]);

  const handleViewTrain = useCallback(() => {
    const sample = allSamples.find(s => s.id === selectedId);
    saveDosingState({
      influentSampleId: selectedId,
      influentSampleName: sample?.sampleName ?? 'Manual Entry',
      influentParams, flowRateMH,
      chemicals: doses.filter(d => d.enabled),
      postDosingParams, savedAt: new Date().toISOString(),
    });
    navigate('/dosing-mixing-train');
  }, [selectedId, allSamples, influentParams, flowRateMH, doses, postDosingParams, navigate]);

  const groups = (Object.keys(GROUP_META) as ChemGroup[]);

  return (
    <div className="min-h-screen bg-rb-bg p-4 md:p-6 space-y-5">

      {/* Header */}
      <PageHeader
        icon={FlaskConical}
        title="Chemical Dosing Station"
        subtitle="Wastewater chemical dosing — all common chemicals with real-time parameter computation"
        iconColor="text-sky-400"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setShowScenarios(true)}
              className="px-3 py-1.5 text-xs font-bold text-amber-300 bg-amber-400/10 border border-amber-400/30 hover:bg-amber-400/20 rounded-lg transition-colors flex items-center gap-1.5">
              <Lightbulb className="w-3.5 h-3.5" /> Suggest Dosing
            </button>
            <button onClick={resetAll}
              className="px-3 py-1.5 text-xs font-bold text-slate-300 bg-rb-elevated border border-slate-700/60 hover:border-slate-500 rounded-lg transition-colors flex items-center gap-1.5">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
            <button onClick={handleProceedToMixing}
              className="px-3 py-1.5 text-xs font-bold text-black bg-amber-400 hover:bg-amber-300 rounded-lg transition-colors flex items-center gap-1.5 shadow-sm">
              <Settings className="w-3.5 h-3.5" /> Proceed to Mixing
            </button>
            <button onClick={handleViewTrain}
              className="px-3 py-1.5 text-xs font-bold text-sky-400 bg-sky-500/10 hover:bg-sky-500/20 border border-sky-800/40 rounded-lg transition-colors flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5" /> View Train
            </button>
          </div>
        }
      />

      {/* 3-column layout */}
      <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr_320px] gap-5">

        {/* ── LEFT: Influent ── */}
        <div className="space-y-4">
          {/* Sample picker */}
          <div className="rb-section p-4 space-y-3">
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
              <Droplets className="w-3.5 h-3.5 text-blue-400" /> Influent Source
            </h2>
            {/* Flow rate */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-slate-300">Flow rate Q</label>
                <div className="flex items-center gap-1">
                  <input type="number" value={flowRateMH} min={0.1} max={10000} step={1}
                    onChange={e => setFlowRateMH(parseFloat(e.target.value) || 1)}
                    className="w-20 text-right text-xs px-2 py-0.5 text-slate-100 bg-rb-surface border border-slate-700/60 rounded-lg focus:outline-none focus:border-sky-500" />
                  <span className="text-[11px] text-slate-500">m³/h</span>
                </div>
              </div>
              <input type="range" min={0.1} max={1000} step={1} value={Math.min(flowRateMH, 1000)}
                onChange={e => setFlowRateMH(parseFloat(e.target.value))}
                className="w-full h-1.5 accent-sky-500 cursor-pointer" />
            </div>
            {/* Sample list */}
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1.5">
                Session influent samples ({allSamples.length})
              </div>
              {allSamples.length === 0 ? (
                <div className="text-[11px] text-slate-600 p-3 rounded-xl bg-rb-elevated border border-slate-800/40 text-center">
                  No influent samples found.<br />Using default industrial influent.
                </div>
              ) : (
                <div className="space-y-1 max-h-44 overflow-y-auto">
                  {allSamples.map(s => {
                    const active = s.id === selectedId;
                    return (
                      <button key={s.id} onClick={() => setSelectedId(s.id)}
                        className={`w-full text-left px-3 py-2 rounded-xl border-2 transition-all text-xs ${
                          active ? 'border-sky-500 bg-sky-500/8' : 'border-slate-800/60 bg-rb-elevated hover:border-slate-700'
                        }`}>
                        <div className="font-bold text-slate-200 truncate">{s.sampleName}</div>
                        <div className="text-[10px] text-slate-500">
                          {new Date(s.createdAt.replace(' ','T')).toLocaleDateString('en-GB')}
                          {active && ' · Selected'}
                        </div>
                      </button>
                    );
                  })}
                  <button onClick={() => setSelectedId(null)}
                    className={`w-full text-left px-3 py-2 rounded-xl border-2 transition-all text-xs ${
                      selectedId === null ? 'border-amber-500 bg-amber-500/8' : 'border-slate-800/60 bg-rb-elevated hover:border-slate-700'
                    }`}>
                    <div className="font-bold text-slate-400">Default Industrial Influent</div>
                    <div className="text-[10px] text-slate-500">Manual / example values</div>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Influent parameters display */}
          <div className="rb-section p-4 space-y-2">
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Influent Parameters</h2>
            {PARAM_DEFS.map(pd => {
              const v = influentParams[pd.key] ?? 0;
              return v > 0 ? (
                <div key={pd.key} className="flex justify-between items-center px-2.5 py-1.5 rounded-lg bg-rb-elevated border border-slate-800/40 text-xs">
                  <span className="text-slate-400">{pd.label}</span>
                  <span className="font-mono font-bold text-blue-400">{fv(v, v < 1 ? 3 : v < 10 ? 2 : 1)} <span className="text-[10px] text-slate-600 font-normal">{pd.unit}</span></span>
                </div>
              ) : null;
            })}
          </div>
        </div>

        {/* ── CENTRE: Chemical Doses ── */}
        <div className="space-y-3">
          {/* Summary bar */}
          <div className="rb-section px-4 py-3 flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <FlaskConical className="w-4 h-4 text-sky-400" />
              <span className="text-sm font-bold text-slate-200">Chemical Doses</span>
            </div>
            <div className="flex items-center gap-3 ml-auto flex-wrap">
              <div className="text-xs text-slate-400">
                <span className="font-bold text-slate-200">{enabledChemicals.length}</span> chemicals active
              </div>
              <div className="text-xs text-slate-400">
                Total dose: <span className="font-bold text-amber-400">{fv(totalDose, 1)} mg/L</span>
              </div>
              <div className="text-xs text-slate-400">
                Flow: <span className="font-bold text-sky-400">{flowRateMH} m³/h</span>
                {' · '}
                <span className="font-bold text-slate-300">{fv(totalDose * flowRateMH / 1000, 2)} kg/h</span> total
              </div>
            </div>
          </div>

          {/* Chemical groups */}
          {groups.map(group => {
            const meta     = GROUP_META[group];
            const GroupIcon= meta.icon;
            const groupChems = CHEMICALS.filter(c => c.group === group);
            const active   = groupChems.some(c => doses.find(d => d.id === c.id)?.enabled);
            const isOpen   = openGroups[group];
            return (
              <div key={group} className="rb-section overflow-hidden">
                <button onClick={() => toggleGroup(group)}
                  className="w-full flex items-center justify-between px-4 py-3 hover:bg-rb-elevated transition-colors">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                      style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}30` }}>
                      <GroupIcon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                    </div>
                    <span className="text-sm font-bold text-slate-200">{meta.label}</span>
                    {active && (
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full"
                        style={{ background: `${meta.color}20`, color: meta.color, border: `1px solid ${meta.color}30` }}>
                        ACTIVE
                      </span>
                    )}
                  </div>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 space-y-2 border-t border-slate-800/50">
                    <div className="pt-3 space-y-2">
                      {groupChems.map(def => {
                        const d = doses.find(x => x.id === def.id)!;
                        return (
                          <ChemicalRow key={def.id} def={def} dose={d.dose} enabled={d.enabled}
                            onToggle={() => toggleChem(def.id)}
                            onChange={v => setDose(def.id, v)} />
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* ── RIGHT: Post-Dosing Parameters ── */}
        <div className="space-y-4">
          <div className="rb-section p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> Post-Dosing Water
              </h2>
              <span className="text-[10px] text-slate-500">Influent → Dosed</span>
            </div>
            <div className="space-y-1.5">
              {PARAM_DEFS.map(pd => {
                const before = influentParams[pd.key] ?? 0;
                const after  = postDosingParams[pd.key] ?? before;
                return (
                  <ParamDelta key={pd.key} label={pd.label} before={before} after={after}
                    unit={pd.unit} lowerBetter={pd.lowerBetter} />
                );
              })}
            </div>
          </div>

          {/* Chemical mass flow summary */}
          {enabledChemicals.length > 0 && (
            <div className="rb-section p-4 space-y-2">
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2">
                <Package className="w-3.5 h-3.5 text-amber-400" /> Chemical Mass Flow
              </h2>
              <div className="text-[10px] text-slate-600 mb-2">At Q = {flowRateMH} m³/h</div>
              {enabledChemicals.map(d => {
                const def = CHEMICALS.find(c => c.id === d.id)!;
                const kgPerH = (d.dose * flowRateMH) / 1000;
                const kgPerDay = kgPerH * 24;
                return (
                  <div key={d.id} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-rb-elevated border border-slate-800/40 text-xs">
                    <div>
                      <span className="text-slate-300 font-medium">{def.name}</span>
                      <span className="text-slate-600 ml-1.5">{d.dose} {def.unit}</span>
                    </div>
                    <div className="text-right font-mono">
                      <div className="font-bold" style={{ color: def.color }}>{fv(kgPerH, 2)} kg/h</div>
                      <div className="text-[10px] text-slate-600">{fv(kgPerDay, 1)} kg/d</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Warnings */}
          {postDosingParams.ph < 6 && (
            <div className="flex items-start gap-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20 px-3.5 py-3">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-200/80">
                <strong className="text-amber-300">Low pH warning:</strong> Post-dosing pH = {fv(postDosingParams.ph, 2)} — corrosive to infrastructure. Add base to neutralise.
              </p>
            </div>
          )}
          {postDosingParams.ph > 10.5 && (
            <div className="flex items-start gap-2.5 rounded-xl bg-rose-500/5 border border-rose-500/20 px-3.5 py-3">
              <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-rose-200/80">
                <strong className="text-rose-300">High pH warning:</strong> pH = {fv(postDosingParams.ph, 2)} — scale formation risk. Check alkalinity and Ca hardness.
              </p>
            </div>
          )}
          {totalDose > 500 && (
            <div className="flex items-start gap-2.5 rounded-xl bg-amber-500/5 border border-amber-500/20 px-3.5 py-3">
              <Info className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-200/70">
                Total dose {fv(totalDose, 0)} mg/L is high — verify dosing rates and check solubility limits. Consider staged addition.
              </p>
            </div>
          )}

          {/* Proceed button */}
          <button onClick={handleProceedToMixing}
            className="w-full py-3 rounded-xl bg-amber-400 hover:bg-amber-300 text-black text-sm font-bold transition-colors flex items-center justify-center gap-2 shadow-sm">
            <Wind className="w-4 h-4" /> Proceed to Impeller Mixing
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Dosing Scenario Modal */}
      {showScenarios && (
        <DosingScenarioModal
          scenarios={dosingScenarios}
          influent={influentParams}
          onApply={applyScenario}
          onClose={() => setShowScenarios(false)}
        />
      )}
    </div>
  );
}
